import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { haptic } from '@/lib/haptics';

interface DailyTaskCardProps {
  icon: string;
  title: string;
  status: 'pending' | 'done' | 'locked' | 'optional' | 'pending_review';
  description: string;
  action?: () => void;
  onEdit?: () => void;
  expandable?: boolean;
  defaultExpanded?: boolean;
  children?: React.ReactNode;
}

export function DailyTaskCard({
  icon, title, status, description, action, onEdit, expandable, defaultExpanded, children
}: DailyTaskCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded || false);

  const statusStyles = {
    pending: 'border-primary/50 bg-primary/5',
    done: 'border-success/50 bg-success/5',
    locked: 'border-gray-700 bg-dark-lighter opacity-50',
    optional: 'border-gray-700 bg-dark-card',
    pending_review: 'border-yellow-500/50 bg-yellow-500/5',
  };

  return (
    <div className={cn(
      'rounded-2xl border-2 overflow-hidden transition-all',
      statusStyles[status]
    )}>
      <div
        className={cn(
          'p-4 flex items-center gap-4 transition-all',
          (action || expandable) && status !== 'locked' && 'cursor-pointer active:bg-white/5 active:scale-[0.99]'
        )}
        onClick={() => {
          if (status === 'locked') return;
          if (expandable) {
            haptic.light();
            setExpanded(!expanded);
          } else if (action) {
            haptic.light();
            action();
          }
        }}
      >
        <div className={cn(
          'w-14 h-14 rounded-xl flex items-center justify-center text-3xl',
          status === 'done' ? 'bg-success/20' :
          status === 'locked' ? 'bg-gray-800' :
          status === 'pending_review' ? 'bg-yellow-500/20' : 'bg-dark-lighter'
        )}>
          {status === 'done' ? '✓' : status === 'pending_review' ? '⏳' : icon}
        </div>

        <div className="flex-1">
          <h3 className="font-bold text-white text-lg">{title}</h3>
          <p className="text-sm text-gray-400">{description}</p>
        </div>

        {status === 'done' && onEdit && (
          <button
            onClick={(e) => { e.stopPropagation(); haptic.light(); onEdit(); }}
            className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center transition-all active:scale-[0.97]"
          >
            ✏️
          </button>
        )}

        {(action || expandable) && status !== 'locked' && (
          <div className="text-2xl text-gray-400">
            {expandable ? (expanded ? '▼' : '▶') : '→'}
          </div>
        )}
      </div>

      {expandable && expanded && (
        <div className="border-t border-gray-700/50 p-4 bg-dark-lighter/50">
          {children}
        </div>
      )}
    </div>
  );
}
